import { RefObject, useRef } from "react";
import { RouteResultForMapView } from "../types/routing";
import SpeechBubbleButton from "@/features/pathFinding/components/SpeechBubbleButton";
import { createRoot, type Root } from "react-dom/client";
import * as maplibregl from "maplibre-gl";

export const useFloorJumpSpeechBubble = (map: RefObject<maplibregl.Map | null>) => {
  const currentFloorJumpSpeechBubbles = useRef<maplibregl.Marker[] | null>(null);
  // unmount しないと i18n の購読などが残り、経路を出すたびに root ごとリークする
  const currentRoots = useRef<Root[]>([]);

  const displayFloorJumpSpeechBubble = (
    floorId: string,
    newRoute: RouteResultForMapView,
    onChangeFloor: (floorId: string) => Promise<void>,
  ): void => {
    if (!map.current || newRoute.floorJumpDatas === undefined) return;

    clearFloorJumpSpeechBubble();

    const onChangeFloorWithJumpToLinkedPlace = async (floorId: string) => {
      await onChangeFloor(floorId);
    };

    currentFloorJumpSpeechBubbles.current = newRoute.floorJumpDatas.map((floorJumpData) => {
      // // 1. マーカーのコンテナとなるDOM要素を作成
      const markerContainer = document.createElement("div");

      // // 2. React Root を作成し、コンテナにコンポーネントをレンダリング
      const root = createRoot(markerContainer);
      currentRoots.current.push(root);
      root.render(
        <SpeechBubbleButton
          n={floorJumpData.numberOfFloorGoingTo}
          direction={floorJumpData.direction}
          variant={floorJumpData.type}
          animate={floorJumpData.type !== "return"}
        />,
      );

      const floorJumpSpeechBubble = new maplibregl.Marker({
        element: markerContainer,
        anchor: "bottom",
      })
        .setLngLat([floorJumpData.fromNodePosition.x, floorJumpData.fromNodePosition.y])
        .addTo(map.current!);

      floorJumpSpeechBubble.getElement().dataset.floorId = floorJumpData.floorIdGoingFrom;

      floorJumpSpeechBubble.getElement().addEventListener("click", () => {
        onChangeFloorWithJumpToLinkedPlace(floorJumpData.floorIdGoingTo);
      });

      return floorJumpSpeechBubble;
    });

    updateFloorJumpSpeechBubbleForFloor(floorId);
  };

  const updateFloorJumpSpeechBubbleForFloor = (floorId: string) => {
    if (!map.current || !currentFloorJumpSpeechBubbles.current) return;

    currentFloorJumpSpeechBubbles.current.forEach((floorJumpSpeechBubble) => {
      if (floorJumpSpeechBubble.getElement().dataset.floorId === floorId) {
        floorJumpSpeechBubble.addTo(map.current!);
      } else {
        floorJumpSpeechBubble.remove();
      }
    });
  };

  const clearFloorJumpSpeechBubble = () => {
    if (currentRoots.current.length > 0) {
      const rootsToUnmount = currentRoots.current;
      currentRoots.current = [];
      // 描画中に同期 unmount すると React が警告するため次のタスクで行う
      setTimeout(() => rootsToUnmount.forEach((root) => root.unmount()), 0);
    }

    if (!currentFloorJumpSpeechBubbles.current) return;

    currentFloorJumpSpeechBubbles.current.forEach((floorJumpSpeechBubble) => {
      floorJumpSpeechBubble.remove();
    });
    currentFloorJumpSpeechBubbles.current = null;
  };

  return {
    displayFloorJumpSpeechBubble,
    clearFloorJumpSpeechBubble,
    updateFloorJumpSpeechBubbleForFloor,
  };
};
